import React, { cloneElement, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Flex, Form, Input, Tabs, TabsProps, Tooltip } from 'antd';
import {
  FormContainer,
  FormSectionTitle,
  READ_ONLY_INPUT_STYLE,
} from '@leedpulse-monorepo/components';
import { IAdmin } from '@leedpulse-monorepo/interfaces';
import { FormOutlined, JavaScriptOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib';
import { useDebounce } from '@leedpulse-monorepo/hooks';
import AdminGeneratedTab from './tabs/AdminGeneratedTab';
import AdminNewTabModal from './tabs/AdminNewTabModal';
import { getAdminTabLabel } from './AdminFormInitialValues';

// Code editor
import Editor from 'react-simple-code-editor';
import { highlight, languages } from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css';

const EXCLUDED_TOP_LEVEL_KEYS = new Set(['id', 'env', 'createdAt', 'snapshot']);

const ADD_TAB_KEY = '__add_tab__';

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  v != null && typeof v === 'object' && !Array.isArray(v);

export interface AdminFormFieldsRef {
  getPayload: () => Record<string, unknown>;
}

interface AdminFormFieldsProps {
  form: FormInstance<IAdmin>;
  isCreateMode?: boolean;
  isReadOnly?: boolean;
}

const AdminFormFields = forwardRef<AdminFormFieldsRef, AdminFormFieldsProps>(({
  form,
  isCreateMode = false,
  isReadOnly = false,
}, ref) => {
  const [isJsonMode, setIsJsonMode] = useState<boolean>(false);
  const [debouncedJson, setDebouncedJson] = useDebounce("", 500, () => {
    handleJsonChange(debouncedJson);
  });
  const [activeKey, setActiveKey] = useState<string>();
  const [isNewTabModalOpen, setIsNewTabModalOpen] = useState<boolean>(false);
  const dragKeyRef = useRef<string | null>(null);

  // tabSchemaRef is always current — avoids stale closures in useImperativeHandle
  const tabSchemaRef = useRef<Record<string, unknown>>(
    form.getFieldsValue(true) as Record<string, unknown>
  );
  const [tabSchema, setTabSchemaState] = useState<Record<string, unknown>>(
    tabSchemaRef.current
  );

  const setTabSchema = (schema: Record<string, unknown>) => {
    tabSchemaRef.current = schema;
    setTabSchemaState(schema);
  };

  // Builds the authoritative payload:
  // - tabSchema drives the structure recursively (removed keys, even nested, are excluded)
  // - form store provides the leaf values (includes form-mode edits)
  const pickBySchema = (schemaNode: unknown, valueNode: unknown): unknown => {
    if (!isPlainObject(schemaNode)) {
      return valueNode !== undefined ? valueNode : schemaNode;
    }
    const out: Record<string, unknown> = {};
    const valueObj = isPlainObject(valueNode) ? valueNode : {};
    for (const k of Object.keys(schemaNode)) {
      out[k] = pickBySchema(schemaNode[k], valueObj[k]);
    }
    return out;
  };

  const buildPayload = (): Record<string, unknown> => {
    const formValues = form.getFieldsValue(true) as Record<string, unknown>;
    const schema = tabSchemaRef.current;

    const payload: Record<string, unknown> = {
      id: formValues['id'],
    };

    Object.keys(schema)
      .filter((k) => !EXCLUDED_TOP_LEVEL_KEYS.has(k))
      .forEach((key) => {
        payload[key] = pickBySchema(schema[key], formValues[key]);
      });

    return payload;
  };

  const removeField = (path: (string | number)[]): void => {
    if (path.length === 0) return;
    const next = structuredClone(tabSchemaRef.current) as Record<string, unknown>;
    let node: unknown = next;
    for (let i = 0; i < path.length - 1; i++) {
      if (!isPlainObject(node)) return;
      node = node[path[i]];
    }
    if (isPlainObject(node)) {
      delete node[path[path.length - 1]];
      setTabSchema(next);
    }
  };

  const addField = (path: (string | number)[], value: unknown): void => {
    if (path.length === 0) return;
    const next = structuredClone(tabSchemaRef.current) as Record<string, unknown>;
    let node: unknown = next;
    for (let i = 0; i < path.length - 1; i++) {
      if (!isPlainObject(node)) return;
      node = node[path[i]];
    }
    if (isPlainObject(node)) {
      node[path[path.length - 1]] = value;
      setTabSchema(next);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setFieldValue(path, value as any);
    }
  };

  const removeTab = (tabKey: string): void => {
    const next = structuredClone(tabSchemaRef.current) as Record<string, unknown>;
    delete next[tabKey];
    setTabSchema(next);
  };

  useImperativeHandle(ref, () => ({
    getPayload: buildPayload,
  }));

  const tabItems = useMemo(() => {
    return Object.keys(tabSchema)
      .filter((k) => !EXCLUDED_TOP_LEVEL_KEYS.has(k))
      .map((key) => {
        const tabValue = (tabSchema[key] ?? {}) as Record<string, unknown>;
        const label = getAdminTabLabel((tabValue['label'] as string) || key);
        const index = (tabValue['index'] as number) || 0;
        return {
          key,
          label,
          index,
          children: <AdminGeneratedTab tabKey={key} tabValue={tabValue} isReadOnly={isReadOnly} onRemove={isReadOnly ? undefined : removeField} onAdd={isReadOnly ? undefined : addField} onRemoveTab={isReadOnly ? undefined : () => removeTab(key)} />,
          forceRender: true,
        };
      }).sort((a, b) => (b.index - a.index));
  }, [tabSchema]);

  const addTab = (tab: { key: string; label: string; index: number }): void => {
    const next = structuredClone(tabSchemaRef.current) as Record<string, unknown>;

    Object.keys(next).forEach((k) => {
      if (EXCLUDED_TOP_LEVEL_KEYS.has(k)) return;
      const node = next[k];
      if (isPlainObject(node) && typeof node['index'] === 'number' && node['index'] >= tab.index) {
        node['index'] = node['index'] + 1;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.setFieldValue([k, 'index'], node['index'] as any);
      }
    });

    next[tab.key] = { key: tab.key, label: tab.label, index: tab.index };
    setTabSchema(next);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form.setFieldValue([tab.key, 'key'], tab.key as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form.setFieldValue([tab.key, 'label'], tab.label as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    form.setFieldValue([tab.key, 'index'], tab.index as any);
    setActiveKey(tab.key);
  };

  const reorderTabs = (fromKey: string, toKey: string): void => {
    if (fromKey === toKey) return;
    const orderedKeys = tabItems.map((t) => t.key);
    const from = orderedKeys.indexOf(fromKey);
    const to = orderedKeys.indexOf(toKey);
    if (from === -1 || to === -1) return;
    orderedKeys.splice(to, 0, orderedKeys.splice(from, 1)[0]);

    const next = structuredClone(tabSchemaRef.current) as Record<string, unknown>;
    const total = orderedKeys.length;
    orderedKeys.forEach((k, i) => {
      const node = next[k];
      if (!isPlainObject(node)) return;
      const index = total - i; // descending sort
      node['index'] = index;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setFieldValue([k, 'index'], index as any);
    });
    setTabSchema(next);
  };

  const renderTabBar: NonNullable<TabsProps['renderTabBar']> = (tabBarProps, DefaultTabBar) => (
    <DefaultTabBar {...tabBarProps}>
      {(node) => {
        const element = node as React.ReactElement;
        const key = element.key as string;
        if (key === ADD_TAB_KEY) return node;
        return cloneElement(element, {
          draggable: true,
          onDragStart: () => { dragKeyRef.current = key; },
          onDragOver: (e: React.DragEvent) => e.preventDefault(),
          onDrop: () => {
            if (dragKeyRef.current) reorderTabs(dragKeyRef.current, key);
            dragKeyRef.current = null;
          },
          style: { ...element.props.style, cursor: 'move' },
        });
      }}
    </DefaultTabBar>
  );

  const nextTabIndex = tabItems.length ? Math.max(...tabItems.map((t) => t.index)) + 1 : 0;
  const tabModalNotAllowedKeys = [...tabItems.map((t) => t.key), ...EXCLUDED_TOP_LEVEL_KEYS];
  const resolvedActiveKey = activeKey && tabItems.some((t) => t.key === activeKey) ? activeKey : tabItems[0]?.key;
  const tabBarItems = isReadOnly ? tabItems : [...tabItems, { key: ADD_TAB_KEY, label: <PlusCircleOutlined /> }];

  const handleTabChange = (key: string) => {
    if (key === ADD_TAB_KEY) {
      setIsNewTabModalOpen(true);
      return;
    }
    setActiveKey(key);
  };

  const handleJsonChange = (json: string) => {
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      form.resetFields();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      form.setFieldsValue(parsed as any);
      setTabSchema(parsed);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!isJsonMode) return;
    // Use buildPayload (tabSchema-based) so removed keys don't reappear in the JSON editor
    setDebouncedJson(JSON.stringify(buildPayload(), null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJsonMode]);

  return (
    <>
      <Flex align="center" justify="space-between" gap={8}>
        <FormSectionTitle title="Informations générales" />
        <Tooltip title={`Éditer en ${!isJsonMode ? "formulaire" : "format JSON"}`}>
          <Button icon={isJsonMode ? <FormOutlined /> : <JavaScriptOutlined />} onClick={() => setIsJsonMode((o) => !o)} />
        </Tooltip>
      </Flex>

      <Form.Item<IAdmin>
        label="JSON"
        style={{ display: isJsonMode ? 'block' : 'none' }}
      >
        <Editor
          value={debouncedJson}
          onValueChange={(value) => setDebouncedJson(value)}
          readOnly={isReadOnly}
          highlight={(code) => highlight(code, languages.javascript, 'javascript')}
          padding={10}
          lang='json'
          style={{
            background: '#0d1117',
            borderWidth: 1,
            borderStyle: 'solid',
            borderRadius: 6,
            borderColor: '#e6f7f0',
            overflow: 'auto',
            caretColor: '#e6f7f0',
          }}
        />
      </Form.Item>
      <div style={{ display: isJsonMode ? 'none' : 'block' }}>
        <FormContainer>
          <Form.Item<IAdmin>
            label="ID"
            name="id"
            rules={[{ required: true, message: "L'identifiant est requis" }]}
          >
            <Input
              readOnly={!isCreateMode || isReadOnly}
              style={!isCreateMode || isReadOnly ? READ_ONLY_INPUT_STYLE : {}}
            />
          </Form.Item>
        </FormContainer>

        <Tabs
          activeKey={resolvedActiveKey}
          onChange={handleTabChange}
          items={tabBarItems}
          renderTabBar={isReadOnly ? undefined : renderTabBar}
        />

        <AdminNewTabModal
          open={isNewTabModalOpen}
          onCancel={() => setIsNewTabModalOpen(false)}
          notAllowedKeys={tabModalNotAllowedKeys}
          defaultIndex={nextTabIndex}
          onAdded={(tab) => {
            addTab(tab);
            setIsNewTabModalOpen(false);
          }}
        />
      </div>
    </>
  );
});

AdminFormFields.displayName = 'AdminFormFields';

export default AdminFormFields;
